import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type { Apollo } from 'src/gql/generated';

type ReportQuestion = Apollo.GetReportQuestionsQuery['extractionQuestions'][number];

interface ReportQuestionsContextValue {
  activeReportCellPopoverId: string | null;
  questions: ReportQuestion[];
  setActiveReportCellPopoverId: (popoverId: string | null) => void;
}

const ReportQuestionsContext = createContext<ReportQuestionsContextValue | null>(null);

interface ReportQuestionsProviderProps {
  children: React.ReactNode;
  questions: ReportQuestion[];
}

export function ReportQuestionsProvider({
  children,
  questions,
}: ReportQuestionsProviderProps): JSX.Element {
  const [activeReportCellPopoverId, setActiveReportCellPopoverId] = useState<string | null>(null);
  const value = useMemo(
    () => ({
      activeReportCellPopoverId,
      questions,
      setActiveReportCellPopoverId,
    }),
    [activeReportCellPopoverId, questions]
  );

  return (
    <ReportQuestionsContext.Provider value={value}>{children}</ReportQuestionsContext.Provider>
  );
}

export function useReportContext(): ReportQuestionsContextValue {
  const context = useContext(ReportQuestionsContext);
  if (!context) throw new Error('useReportQuestions must be used within a ReportQuestionsProvider');

  return context;
}

export function useReportCellPopoverOpen(popoverId: string) {
  const { activeReportCellPopoverId, setActiveReportCellPopoverId } = useReportContext();
  const open = activeReportCellPopoverId === popoverId;
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      setActiveReportCellPopoverId(nextOpen ? popoverId : null);
    },
    [popoverId, setActiveReportCellPopoverId]
  );

  return { open, setOpen };
}
